import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { uniq, without } from 'lodash-es';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { Link } from 'site-modules/shared/components/link/link';
import { useHorizontalScroll } from 'site-modules/shared/hooks/use-horizontal-scroll';

import './anchor-nav-content.scss';

const ARROW_CONTAINER_CLASS_NAME = 'arrow pos-a top-0 bottom-0 align-items-center';

export function AnchorNavContent({
  isMobile = false,
  navList,
  linkTrackingId = 'view_content',
  ariaLabel = 'skip to section',
  className = null,
  listClassName = null,
  itemClassName = 'ps-2 size-16',
  linkClassName = 'pt-1 pb-0_75',
  subLinkClassName = 'pt-1 pb-0_75',
  arrowClassName = 'icon btn btn-link border-0 text-decoration-none text-primary-darker h-100 px-0_5 px-md-0_75',
  subArrowClassName = null,
  activeClass = 'link-active text-primary-darker',
  creativeId = null,
  addTitleAttr = false,
  hideArrowBtns = false,
  noScroll = false,
  useItemClassName = true,
  useNoFollow = true,
  isHashTracking = false,
  defaultActiveHash = null,
}) {
  const navBarRef = useRef(null);
  const {
    showLeftArrow: leftArrow,
    showRightArrow: rightArrow,
    scrollToStart,
    scrollToEnd,
    scrollToCenter,
    handleDragStart,
    handleScroll,
  } = useHorizontalScroll({ containerRef: navBarRef, enableDrag: !isMobile });

  const [activeHash, setActiveHash] = useState(defaultActiveHash || null);
  const activeItems = useRef(defaultActiveHash ? [defaultActiveHash] : []);

  const navListLength = navList?.length;
  const linkRefs = useMemo(() => {
    const refs = {};

    navList?.forEach?.(({ hash }) => {
      refs[hash] = React.createRef();
    });

    return refs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navListLength]);

  /**
   * Scrolls to center the link for the current active section
   * @param currentHash ID of a section
   */
  const scrollToCurrent = useCallback(
    currentHash => {
      const currentLink = linkRefs[currentHash]?.current;
      if (currentLink) {
        scrollToCenter(currentLink);
      }
    },
    [linkRefs, scrollToCenter]
  );

  const handleSetActive = useCallback(
    newActiveHash => {
      setActiveHash(newActiveHash);
      activeItems.current = uniq([...activeItems.current, newActiveHash]);

      scrollToCurrent(newActiveHash);
    },
    [scrollToCurrent]
  );

  const handleSetInactive = useCallback(
    newInactiveHash => {
      if (newInactiveHash === activeItems.current[activeItems.current.length - 1]) {
        setActiveHash(activeItems.current[activeItems.current.length - 2]);
      }
      activeItems.current = without(activeItems.current, newInactiveHash);
    },
    [defaultActiveHash]
  );

  return (
    <div
      className={classnames('anchor-nav-content pos-r overflow-x-hidden', className)}
      data-tracking-parent={creativeId}
    >
      <div
        className={classnames(ARROW_CONTAINER_CLASS_NAME, subArrowClassName, 'left left-0 pe-1_5', {
          'd-flex': leftArrow,
          'd-none': !leftArrow,
        })}
      >
        {!hideArrowBtns && (
          <button
            className={classnames(arrowClassName, 'icon-arrow-left3 ps-0_25')}
            onClick={scrollToStart}
            aria-label="More Links"
          />
        )}
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <nav
        className={classnames('navbar p-0 text-nowrap heading-6 fw-normal flex-column align-items-stretch', {
          'overflow-x-hidden': !isMobile,
          'fade-left': leftArrow,
          'fade-right': rightArrow,
        })}
        ref={navBarRef}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onScroll={handleScroll}
        aria-label={ariaLabel}
      >
        <ul
          className={classnames('list-unstyled text-start mb-0', listClassName, {
            'ms-0': !useItemClassName,
          })}
          data-tracking-id={linkTrackingId}
        >
          {navList.map(({ name, hash, customOffset }, index) => {
            const trackingValue = isHashTracking ? hash : name;

            return (
              <li
                key={name}
                className={classnames('nav-item d-inline-block', useItemClassName && itemClassName, {
                  'ps-0 size-16': index === 0 && !useItemClassName,
                  'ps-2 size-16': index > 0 && !useItemClassName,
                })}
                ref={linkRefs[hash]}
              >
                {noScroll ? (
                  <Link
                    to={`${hash}/`}
                    rel={useNoFollow ? 'nofollow' : null}
                    className={classnames('d-inline-block text-nowrap sub-navigation-link', subLinkClassName)}
                    data-tracking-value={trackingValue}
                    title={name}
                  >
                    {name}
                  </Link>
                ) : (
                  <ScrollLink
                    spy
                    to={hash}
                    scrollConfig={{ offset: customOffset || SCROLL_OFFSET }}
                    className={classnames('d-inline-block text-nowrap sub-navigation-link', linkClassName, {
                      [activeClass]: hash === activeHash,
                    })}
                    data-tracking-value={trackingValue}
                    onSetActive={handleSetActive}
                    onSetInactive={handleSetInactive}
                    rel={useNoFollow ? 'nofollow' : null}
                    title={addTitleAttr ? name : undefined}
                  >
                    {name}
                  </ScrollLink>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
      <div
        className={classnames(
          ARROW_CONTAINER_CLASS_NAME,
          subArrowClassName,
          'right right-0 justify-content-end ps-1_5',
          {
            'd-flex': rightArrow,
            'd-none': !rightArrow,
          }
        )}
      >
        {!hideArrowBtns && (
          <button
            className={classnames(arrowClassName, 'icon-arrow-right3 pe-0_25')}
            onClick={scrollToEnd}
            aria-label="More Links"
          />
        )}
      </div>
    </div>
  );
}

AnchorNavContent.propTypes = {
  navList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      hash: PropTypes.string,
    })
  ).isRequired,
  linkTrackingId: PropTypes.string,
  isMobile: PropTypes.bool,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  listClassName: PropTypes.string,
  itemClassName: PropTypes.string,
  linkClassName: PropTypes.string,
  subLinkClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  subArrowClassName: PropTypes.string,
  activeClass: PropTypes.string,
  creativeId: PropTypes.string,
  addTitleAttr: PropTypes.bool,
  hideArrowBtns: PropTypes.bool,
  noScroll: PropTypes.bool,
  useItemClassName: PropTypes.bool,
  useNoFollow: PropTypes.bool,
  isHashTracking: PropTypes.bool,
  defaultActiveHash: PropTypes.string,
};
